import './_profile.style.scss';

import { faLock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Modal, notification, Spin } from 'antd';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import colors from '../../../foundation/assets/colors';
import Password from '../../../foundation/components/password/Password';
import { useAppDispatch } from '../../../store/hooks';
import { changePassword, getPasswordRules } from '../redux/async_thunks';
import { selectUser } from '../redux/selectors';
import { ChangePasswordValidationSchema } from './profile_validation';

type ChangePasswordModalProps = {
  onClose: () => void;
  forced?: boolean;
};

const ChangePasswordModal = ({
  onClose,
  forced = false,
}: ChangePasswordModalProps) => {
  const dispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const [passwordRules, setPasswordRules] = useState<string[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);

  useEffect(() => {
    dispatch(getPasswordRules())
      .unwrap()
      .then((rules) => setPasswordRules(rules))
      .catch(() => {
        /* silently hide the box on failure */
      })
      .finally(() => setRulesLoading(false));
  }, []);

  const formik = useFormik({
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validationSchema: ChangePasswordValidationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        await dispatch(
          changePassword({
            current_password: values.current_password,
            new_password: values.new_password,
          }),
        ).unwrap();

        notification.success({
          message: 'Password changed',
          description: 'Your password has been updated successfully.',
        });
        onClose();
      } catch (error: any) {
        // rejectWithValue passes allauth's response body as the payload:
        // { status: 400, errors: [{ message, code, param }] }
        const allauthErrors: Array<{
          param?: string;
          message: string;
          code: string;
        }> = error?.errors ?? [];

        if (allauthErrors.length) {
          let hasFieldError = false;
          for (const err of allauthErrors) {
            if (
              err.param === 'current_password' ||
              err.code === 'enter_current_password'
            ) {
              setFieldError('current_password', err.message);
              hasFieldError = true;
            } else if (err.param === 'new_password') {
              setFieldError('new_password', err.message);
              hasFieldError = true;
            }
          }
          if (!hasFieldError) {
            notification.error({
              message: 'Password change failed',
              description: allauthErrors.map((e) => e.message).join(' '),
            });
          }
        } else {
          notification.error({
            message: 'Password change failed',
            description: error?.message ?? 'Failed to change password.',
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      open
      closable={false}
      footer={null}
      title={null}
      maskClosable={false}
      centered
      width={420}
      zIndex={3100}
    >
      <div className="l-profile__header">
        <div className="l-profile__header-title-row">
          <div className="l-profile__header-title">
            <FontAwesomeIcon icon={faLock} color={colors.primary} />
            Change Password
          </div>
          {!forced && (
            <div className="l-profile__header-close" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} color={colors.primary} />
            </div>
          )}
        </div>
        {user?.username && (
          <div className="l-profile__header-account-chip">
            <span className="l-profile__header-account-avatar">
              {user.username[0].toUpperCase()}
            </span>
            <span className="l-profile__header-account-name">
              {user.username}
            </span>
          </div>
        )}
      </div>
      {forced && (
        <Alert
          type="warning"
          showIcon
          className="l-change-password__forced-banner"
          message="Please change your password before continuing."
        />
      )}

      <div className="l-change-password__form">
        <div className="l-change-password__field-wrapper">
          <label htmlFor="current_password">Current password</label>
          <Password
            id="current_password"
            name="current_password"
            placeholder="Enter current password"
            value={formik.values.current_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            autoComplete="current-password"
            error={
              formik.touched.current_password
                ? formik.errors.current_password
                : undefined
            }
          />
        </div>

        <div className="l-change-password__field-wrapper">
          <label htmlFor="new_password">New password</label>
          <Password
            id="new_password"
            name="new_password"
            placeholder="Enter new password"
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            autoComplete="new-password"
            error={
              formik.touched.new_password
                ? formik.errors.new_password
                : undefined
            }
          />
        </div>

        {rulesLoading ? (
          <div className="l-change-password__rules-loading">
            <Spin size="small" />
          </div>
        ) : passwordRules.length > 0 ? (
          <Alert
            type="info"
            showIcon
            className="l-change-password__rules-alert"
            message={
              <ul className="l-change-password__rules-list">
                {passwordRules.map((rule, i) => (
                  <li key={i}>{rule}</li>
                ))}
              </ul>
            }
          />
        ) : null}

        <div className="l-change-password__field-wrapper">
          <label htmlFor="confirm_password">Confirm new password</label>
          <Password
            id="confirm_password"
            name="confirm_password"
            placeholder="Repeat new password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            autoComplete="new-password"
            error={
              formik.touched.confirm_password
                ? formik.errors.confirm_password
                : undefined
            }
          />
        </div>

        <div className="l-change-password__actions">
          {!forced && (
            <Button
              className="l-change-password__cancel-btn"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            className="l-change-password__submit-btn"
            type="primary"
            loading={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            Change Password
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
